import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// J4 — Clinical Protocol Compliance

const CATEGORIES = ['pre-op', 'intra-op', 'post-op'];
const emptyStep = () => ({ code: '', label: '', mandatory: true, order: 0, category: 'intra-op' });
const emptyProtocol = () => ({ name: '', treatmentName: '', description: '', steps: [emptyStep()], isActive: true });

const ClinicalProtocolsPage = ({ API_BASE }) => {
  const [tab, setTab] = useState('templates');
  const [protocols, setProtocols] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [byDoctor, setByDoctor] = useState([]);
  const [byTreatment, setByTreatment] = useState([]);
  const [treatmentTypes, setTreatmentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);  // protocol in-edit
  const [recording, setRecording] = useState(null);  // compliance record form

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, c, bd, bt, tt] = await Promise.all([
        axios.get(`${API_BASE}/protocols`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/protocols/compliance?limit=100`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/protocols/compliance/stats/by-doctor`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/protocols/compliance/stats/by-treatment`).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/treatment-types`).catch(() => ({ data: [] })),
      ]);
      setProtocols(p.data || []);
      setCompliance(c.data || []);
      setByDoctor(bd.data || []);
      setByTreatment(bt.data || []);
      setTreatmentTypes(tt.data || []);
    } catch (e) { /* per-call swallowed */ }
    finally { setLoading(false); }
  }, [API_BASE]);

  useEffect(() => { load(); }, [load]);

  // ── Protocol template editor ──
  const saveProtocol = async () => {
    if (!editing.name.trim() || !editing.treatmentName.trim()) return alert('Name and Treatment are required');
    if (!editing.steps.length) return alert('Add at least one step');
    try {
      if (editing._id) await axios.put(`${API_BASE}/protocols/${editing._id}`, editing);
      else             await axios.post(`${API_BASE}/protocols`, editing);
      setEditing(null); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const deleteProtocol = async (id) => {
    if (!window.confirm('Delete this protocol template?')) return;
    await axios.delete(`${API_BASE}/protocols/${id}`); load();
  };

  const addStep = () => setEditing({ ...editing, steps: [...editing.steps, { ...emptyStep(), order: editing.steps.length }] });
  const updateStep = (i, field, val) => {
    const steps = [...editing.steps];
    steps[i] = { ...steps[i], [field]: val };
    setEditing({ ...editing, steps });
  };
  const removeStep = (i) => {
    const steps = editing.steps.filter((_, idx) => idx !== i);
    setEditing({ ...editing, steps });
  };

  // ── Compliance recording ──
  const startRecording = async (treatmentName) => {
    try {
      const r = await axios.get(`${API_BASE}/protocols/for/${encodeURIComponent(treatmentName)}`);
      const protocol = r.data;
      setRecording({
        treatmentId: '',
        patientId: '',
        patientName: '',
        doctorName: '',
        treatmentName: protocol.treatmentName,
        protocolId: protocol._id,
        protocolName: protocol.name,
        steps: protocol.steps.map(s => ({ code: s.code, label: s.label, mandatory: s.mandatory, status: 'pending', note: '' })),
      });
    } catch (e) { alert('No active protocol for this treatment — define one first.'); }
  };

  const toggleStepStatus = (i, status) => {
    const steps = [...recording.steps];
    steps[i] = { ...steps[i], status, completedAt: status === 'completed' ? new Date() : null };
    setRecording({ ...recording, steps });
  };

  const updateStepNote = (i, note) => {
    const steps = [...recording.steps];
    steps[i] = { ...steps[i], note };
    setRecording({ ...recording, steps });
  };

  const saveCompliance = async () => {
    if (!recording.treatmentId || !recording.patientId) return alert('Treatment ID and Patient ID required (from Treatment record)');
    try {
      await axios.post(`${API_BASE}/protocols/compliance`, { ...recording, completedAt: new Date(), recordedBy: 'admin' });
      setRecording(null); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const scoreColor = (pct) => {
    if (pct >= 90) return { bg:'#dcfce7', fg:'#166534' };
    if (pct >= 75) return { bg:'#e0f2fe', fg:'#075985' };
    if (pct >= 60) return { bg:'#fef3c7', fg:'#92400e' };
    return { bg:'#fee2e2', fg:'#991b1b' };
  };

  return (
    <div style={{ padding:20 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12, flexWrap:'wrap', gap:10 }}>
        <h2 style={{ color: theme.navy, margin:0 }}>🧪 Clinical Protocol Compliance</h2>
      </div>

      <div style={{ ...cardStyle, marginBottom:12, background:theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color:theme.navy, lineHeight:1.5 }}>
          <b>Why:</b> Define standard steps per treatment (e.g. Root Canal → pre-op x-ray → anesthesia → cleaning → obturation → post-op x-ray). Record which steps were followed per case. Identify doctors below 80% adherence for retraining. Reduces variability + medico-legal risk.
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:16 }}>
        {[
          ['templates',  '📋 Protocol Templates'],
          ['compliance', '✅ Compliance Records'],
          ['stats',      '📊 Adherence Stats'],
        ].map(([k, lbl]) => (
          <button key={k} onClick={()=>setTab(k)}
            style={{ padding:'8px 18px', border:'none', borderRadius:8, fontWeight:600, fontSize:'0.85rem', cursor:'pointer',
              background: tab===k ? theme.primary : '#f1f3f5', color: tab===k ? 'white' : '#333' }}>
            {lbl}
          </button>
        ))}
      </div>

      {loading && <div style={{ padding:40, textAlign:'center', color:'#888' }}>Loading…</div>}

      {/* TEMPLATES TAB */}
      {!loading && tab === 'templates' && (
        <div>
          <button onClick={()=>setEditing(emptyProtocol())}
            style={{ ...btnStyle, background:theme.primary, color:'white', marginBottom:12 }}>
            + New Protocol Template
          </button>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            {protocols.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No protocols defined yet. Click above to create one.</div> :
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Protocol Name</th>
                <th style={th}>Treatment</th>
                <th style={th}>Steps</th>
                <th style={th}>Mandatory</th>
                <th style={th}>Active</th>
                <th style={th}>Actions</th>
              </tr></thead>
              <tbody>
                {protocols.map(p => {
                  const mandatory = p.steps.filter(s => s.mandatory).length;
                  return (
                    <tr key={p._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>{p.name}</td>
                      <td style={td}>{p.treatmentName}</td>
                      <td style={td}>{p.steps.length}</td>
                      <td style={td}>{mandatory}/{p.steps.length}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.72rem', fontWeight:700,
                          background: p.isActive ? '#dcfce7' : '#f1f5f9', color: p.isActive ? '#166534' : '#64748b' }}>
                          {p.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={td}>
                        <button style={tinyBtn} onClick={()=>setEditing({ ...p })}>Edit</button>
                        <button style={tinyBtn} onClick={()=>startRecording(p.treatmentName)}>Record</button>
                        <button style={{ ...tinyBtn, background:theme.danger, color:'white', border:'none' }} onClick={()=>deleteProtocol(p._id)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>}
          </div>
        </div>
      )}

      {/* COMPLIANCE TAB */}
      {!loading && tab === 'compliance' && (
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          {compliance.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No compliance records. Record one via Templates tab.</div> :
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#f9fafb' }}>
              <th style={th}>Patient</th><th style={th}>Treatment</th>
              <th style={th}>Doctor</th><th style={th}>Score</th>
              <th style={th}>Mandatory</th><th style={th}>Recorded</th>
            </tr></thead>
            <tbody>
              {compliance.map(c => {
                const sc = scoreColor(c.complianceScore);
                return (
                  <tr key={c._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{c.patientName || '—'}</td>
                    <td style={td}>{c.treatmentName}</td>
                    <td style={td}>{c.doctorName || '—'}</td>
                    <td style={td}>
                      <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.78rem', fontWeight:700, background:sc.bg, color:sc.fg }}>
                        {c.complianceScore}%
                      </span>
                    </td>
                    <td style={td}>{c.mandatoryCompleted}/{c.mandatoryTotal}</td>
                    <td style={{ ...td, fontSize:'0.75rem', color:'#888' }}>{c.completedAt ? new Date(c.completedAt).toLocaleDateString('en-IN') : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>}
        </div>
      )}

      {/* STATS TAB */}
      {!loading && tab === 'stats' && (
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            <div style={headerBar}>👨‍⚕️ By Doctor</div>
            {byDoctor.length === 0 ? <div style={{ padding:30, textAlign:'center', color:'#888' }}>No records yet</div> :
             <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Doctor</th><th style={th}>Cases</th><th style={th}>Avg Score</th>
                <th style={th}>Full ✓</th><th style={th}>&lt;70%</th>
              </tr></thead>
              <tbody>
                {byDoctor.map(d => {
                  const sc = scoreColor(d.avgScore);
                  return (
                    <tr key={d.doctorName} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>{d.doctorName}</td>
                      <td style={td}>{d.total}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.75rem', fontWeight:700, background:sc.bg, color:sc.fg }}>
                          {d.avgScore}%
                        </span>
                      </td>
                      <td style={{ ...td, color:theme.success, fontWeight:600 }}>{d.fullyCompliant}</td>
                      <td style={{ ...td, color: d.below70 > 0 ? theme.danger : '#666', fontWeight:600 }}>{d.below70}</td>
                    </tr>
                  );
                })}
              </tbody>
             </table>}
          </div>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            <div style={headerBar}>💉 By Treatment</div>
            {byTreatment.length === 0 ? <div style={{ padding:30, textAlign:'center', color:'#888' }}>No records yet</div> :
             <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Treatment</th><th style={th}>Cases</th><th style={th}>Avg Score</th><th style={th}>Full ✓</th>
              </tr></thead>
              <tbody>
                {byTreatment.map(t => {
                  const sc = scoreColor(t.avgScore);
                  return (
                    <tr key={t.treatmentName} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>{t.treatmentName}</td>
                      <td style={td}>{t.total}</td>
                      <td style={td}>
                        <span style={{ padding:'3px 10px', borderRadius:12, fontSize:'0.75rem', fontWeight:700, background:sc.bg, color:sc.fg }}>
                          {t.avgScore}%
                        </span>
                      </td>
                      <td style={{ ...td, color:theme.success, fontWeight:600 }}>{t.fullyCompliant}</td>
                    </tr>
                  );
                })}
              </tbody>
             </table>}
          </div>
        </div>
      )}

      {/* Protocol editor modal */}
      {editing && (
        <div style={modalBg} onClick={()=>setEditing(null)}>
          <div style={modalCard} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 14px', color:theme.navy }}>{editing._id ? 'Edit' : 'New'} Protocol</h3>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
              <div>
                <Label>Protocol Name</Label>
                <input value={editing.name} onChange={e=>setEditing({ ...editing, name: e.target.value })} style={inputStyle}/>
              </div>
              <div>
                <Label>Treatment (links to Treatment Types)</Label>
                <input list="tt-list" value={editing.treatmentName} onChange={e=>setEditing({ ...editing, treatmentName: e.target.value })} style={inputStyle}/>
                <datalist id="tt-list">
                  {treatmentTypes.map(t => <option key={t._id} value={t.name || t.treatmentName}/>)}
                </datalist>
              </div>
            </div>
            <div style={{ marginTop:10 }}>
              <Label>Description</Label>
              <input value={editing.description} onChange={e=>setEditing({ ...editing, description: e.target.value })} style={inputStyle}/>
            </div>

            <div style={{ marginTop:14 }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                <Label>Steps ({editing.steps.length})</Label>
                <button onClick={addStep} style={{ ...tinyBtn, background:theme.primary, color:'white', border:'none' }}>+ Add Step</button>
              </div>
              {editing.steps.map((s, i) => (
                <div key={i} style={{ display:'grid', gridTemplateColumns:'0.8fr 2fr 1fr 80px 40px', gap:6, marginBottom:6, alignItems:'center' }}>
                  <input placeholder="code" value={s.code} onChange={e=>updateStep(i, 'code', e.target.value)} style={{ ...inputStyle, fontSize:'0.8rem' }}/>
                  <input placeholder="Label visible to doctor" value={s.label} onChange={e=>updateStep(i, 'label', e.target.value)} style={{ ...inputStyle, fontSize:'0.8rem' }}/>
                  <select value={s.category} onChange={e=>updateStep(i, 'category', e.target.value)} style={{ ...inputStyle, fontSize:'0.8rem' }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <label style={{ fontSize:'0.75rem', display:'flex', gap:4, alignItems:'center' }}>
                    <input type="checkbox" checked={s.mandatory} onChange={e=>updateStep(i, 'mandatory', e.target.checked)}/> Req
                  </label>
                  <button onClick={()=>removeStep(i)} style={{ ...tinyBtn, background:theme.danger, color:'white', border:'none' }}>✕</button>
                </div>
              ))}
            </div>

            <label style={{ marginTop:14, fontSize:'0.85rem', display:'flex', gap:6, alignItems:'center' }}>
              <input type="checkbox" checked={editing.isActive} onChange={e=>setEditing({ ...editing, isActive: e.target.checked })}/>
              Active (only one active protocol per treatment should exist)
            </label>

            <div style={{ marginTop:16, display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={()=>setEditing(null)} style={{ ...btnStyle, background:'#e5e7eb', color:'#333' }}>Cancel</button>
              <button onClick={saveProtocol} style={{ ...btnStyle, background:theme.primary, color:'white' }}>{editing._id ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Compliance record modal */}
      {recording && (
        <div style={modalBg} onClick={()=>setRecording(null)}>
          <div style={modalCard} onClick={e=>e.stopPropagation()}>
            <h3 style={{ margin:'0 0 14px', color:theme.navy }}>Record Compliance — {recording.treatmentName}</h3>
            <div style={{ fontSize:'0.78rem', color:'#666', marginBottom:10 }}>
              Using protocol: <b>{recording.protocolName}</b>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
              <div>
                <Label>Treatment ID</Label>
                <input value={recording.treatmentId} onChange={e=>setRecording({ ...recording, treatmentId: e.target.value })} style={inputStyle} placeholder="From Treatment record"/>
              </div>
              <div>
                <Label>Patient ID</Label>
                <input value={recording.patientId} onChange={e=>setRecording({ ...recording, patientId: e.target.value })} style={inputStyle}/>
              </div>
              <div>
                <Label>Patient Name</Label>
                <input value={recording.patientName} onChange={e=>setRecording({ ...recording, patientName: e.target.value })} style={inputStyle}/>
              </div>
              <div>
                <Label>Doctor Name</Label>
                <input value={recording.doctorName} onChange={e=>setRecording({ ...recording, doctorName: e.target.value })} style={inputStyle}/>
              </div>
            </div>

            <Label>Step Checklist</Label>
            <div style={{ maxHeight:300, overflowY:'auto', border:'1px solid #e5e7eb', borderRadius:8, marginBottom:12 }}>
              {recording.steps.map((s, i) => (
                <div key={i} style={{ padding:'10px 12px', borderBottom:'1px solid #f1f3f5', display:'flex', gap:10, alignItems:'center' }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontWeight:600, fontSize:'0.88rem' }}>
                      {s.label} {s.mandatory && <span style={{ color:theme.danger, fontSize:'0.7rem' }}>*</span>}
                    </div>
                    <input placeholder="Note (optional)" value={s.note} onChange={e=>updateStepNote(i, e.target.value)}
                      style={{ ...inputStyle, fontSize:'0.75rem', marginTop:4 }}/>
                  </div>
                  <div style={{ display:'flex', gap:4 }}>
                    {['completed', 'skipped', 'pending'].map(st => (
                      <button key={st} onClick={()=>toggleStepStatus(i, st)}
                        style={{ padding:'4px 10px', fontSize:'0.72rem', borderRadius:6, border:'1px solid #e5e7eb', cursor:'pointer', fontWeight:600,
                          background: s.status === st ? (st === 'completed' ? theme.success : st === 'skipped' ? theme.warning : '#64748b') : 'white',
                          color: s.status === st ? 'white' : '#333' }}>
                        {st === 'completed' ? '✓' : st === 'skipped' ? '⊘' : '○'} {st}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button onClick={()=>setRecording(null)} style={{ ...btnStyle, background:'#e5e7eb', color:'#333' }}>Cancel</button>
              <button onClick={saveCompliance} style={{ ...btnStyle, background:theme.primary, color:'white' }}>Save Compliance</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Label = ({ children }) => <div style={{ fontSize:'0.72rem', color:theme.textMuted, fontWeight:600, marginBottom:4, textTransform:'uppercase', letterSpacing:'0.04em' }}>{children}</div>;
const headerBar = { padding:'10px 14px', background:'#f9fafb', fontWeight:700, fontSize:'0.88rem', color:theme.navy, borderBottom:'1px solid #e5e7eb' };
const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const tinyBtn = { padding:'4px 10px', fontSize:'0.75rem', border:'1px solid #e5e7eb', background:'white', borderRadius:6, cursor:'pointer', marginRight:4 };
const modalBg = { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:760, width:'100%', maxHeight:'90vh', overflowY:'auto' };

export default ClinicalProtocolsPage;
